import React from 'react'
import { Section, SectionTitle, SubSection, SubSectionTitle, Text, List, Table, CodeBlock } from '../styles'

export default function APIDocSection() {
  return (
    <Section id="api-docs">
      <SectionTitle>
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
          <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm-5 14H4v-4h11v4zm0-5H4V9h11v4zm5 5h-4V9h4v9z"/>
        </svg>
        API Documentation
      </SectionTitle>
      
      <SubSection id="authentication">
        <SubSectionTitle>Authentication</SubSectionTitle>
        <Text>
          The API uses Solana wallet-based authentication to ensure secure access
          to platform resources:
        </Text>
        <CodeBlock>
{`// API Authentication Example
const response = await fetch('https://api.example.com/v1/projects', {
  headers: {
    'Authorization': 'Bearer sk_live_your_api_key',
    'X-Wallet-Address': 'your_solana_wallet_address',
    'X-Request-Signature': 'signed_message',
    'Content-Type': 'application/json'
  }
})`}
        </CodeBlock>
      </SubSection>

      <SubSection id="endpoints">
        <SubSectionTitle>Core Endpoints</SubSectionTitle>
        <Text>
          The platform provides RESTful endpoints for all major functionalities:
        </Text>
        <Table>
          <thead>
            <tr>
              <th>Endpoint</th>
              <th>Method</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>/projects/create</td>
              <td>POST</td>
              <td>Create a new project with agent configuration</td>
            </tr>
            <tr>
              <td>/projects/{'{id}'}</td>
              <td>GET</td>
              <td>Retrieve project details and status</td>
            </tr>
            <tr>
              <td>/agents/deploy</td>
              <td>POST</td>
              <td>Deploy and configure AI agents</td>
            </tr>
            <tr>
              <td>/outputs</td>
              <td>GET</td>
              <td>List all project outputs and deliverables</td>
            </tr>
          </tbody>
        </Table>
      </SubSection>

      <SubSection id="request-examples">
        <SubSectionTitle>Request Examples</SubSectionTitle>
        <Text>
          Example of creating a new project with AI agents:
        </Text>
        <CodeBlock>
{`// Create Project Request
POST /v1/projects/create
{
  "name": "Website Development Project",
  "description": "Create a modern React website",
  "agents": {
    "coders": {
      "count": 3,
      "capabilities": [
        "frontend_development",
        "react",
        "responsive_design"
      ],
      "priority": "high"
    },
    "designers": {
      "count": 2,
      "capabilities": [
        "ui_design",
        "asset_generation",
        "animation"
      ],
      "priority": "medium"
    }
  },
  "requirements": {
    "deadline": "2024-03-01T00:00:00Z",
    "deliverables": [
      {
        "type": "github_repository",
        "specifications": {
          "framework": "react",
          "features": [
            "responsive_layout",
            "dark_mode",
            "animations"
          ]
        }
      },
      {
        "type": "deployment",
        "specifications": {
          "platform": "vercel",
          "domain": "example.com",
          "environment": "production"
        }
      }
    ]
  },
  "configuration": {
    "test_coverage_threshold": 80,
    "performance_targets": {
      "lighthouse_score": 95,
      "load_time": "< 2s"
    }
  }
}`}
        </CodeBlock>
      </SubSection>

      <SubSection id="responses">
        <SubSectionTitle>Response Format</SubSectionTitle>
        <Text>
          All API responses follow a consistent format:
        </Text>
        <CodeBlock>
{`// Success Response
{
  "success": true,
  "data": {
    "project_id": "proj_abc123",
    "status": "initializing",
    "agents": [
      {
        "id": "agent_1",
        "type": "coder",
        "status": "deploying",
        "capabilities": ["frontend_development", "react"]
      }
    ],
    "created_at": "2024-01-20T10:30:00Z",
    "estimated_completion": "2024-01-21T15:00:00Z"
  },
  "meta": {
    "request_id": "req_xyz789",
    "timestamp": "2024-01-20T10:30:00Z"
  }
}

// Error Response
{
  "success": false,
  "error": {
    "code": "INVALID_CONFIGURATION",
    "message": "Invalid agent configuration provided",
    "details": {
      "field": "agents.coders.count",
      "reason": "Must be between 1 and 5"
    }
  },
  "meta": {
    "request_id": "req_xyz789",
    "timestamp": "2024-01-20T10:30:00Z"
  }
}`}
        </CodeBlock>
      </SubSection>

      <SubSection id="rate-limiting">
        <SubSectionTitle>Rate Limiting</SubSectionTitle>
        <Text>
          API access is rate-limited based on subscription tier:
        </Text>
        <Table>
          <thead>
            <tr>
              <th>Tier</th>
              <th>Rate Limit</th>
              <th>Burst Limit</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Free</td>
              <td>100 requests/hour</td>
              <td>150 requests/hour</td>
            </tr>
            <tr>
              <td>Pro</td>
              <td>1000 requests/hour</td>
              <td>1500 requests/hour</td>
            </tr>
            <tr>
              <td>Enterprise</td>
              <td>Custom</td>
              <td>Custom</td>
            </tr>
          </tbody>
        </Table>
      </SubSection>

      <SubSection id="webhooks">
        <SubSectionTitle>Webhooks</SubSectionTitle>
        <Text>
          Subscribe to real-time updates via webhooks:
        </Text>
        <CodeBlock>
{`// Webhook Configuration
POST /v1/webhooks/create
{
  "url": "https://your-domain.com/webhook",
  "events": [
    "project.created",
    "agent.deployed",
    "task.completed",
    "output.ready"
  ],
  "secret": "your_webhook_secret",
  "metadata": {
    "environment": "production",
    "version": "1.0"
  }
}

// Webhook Payload Example
{
  "event": "task.completed",
  "project_id": "proj_abc123",
  "data": {
    "task_id": "task_456",
    "agent_id": "agent_1",
    "result": {
      "status": "success",
      "output_url": "https://github.com/example/repo/commit/abc123",
      "metrics": {
        "completion_time": "1.5h",
        "quality_score": 95
      }
    }
  },
  "timestamp": "2024-01-20T10:35:00Z",
  "signature": "computed_signature_for_verification"
}`}
        </CodeBlock>
      </SubSection>
    </Section>
  )
}
